import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import _ from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import {
  ExternalLeadProduct,
  ExternalLeadStatus,
} from '@tassomutuo-backoffice/types';

interface IProps {
  product: ExternalLeadProduct;
  q: string;
  status: ExternalLeadStatus | '';
  createdFrom: string;
  createdTo: string;
  onChangeQ: (value: string) => void;
  onChangeStatus: (value: ExternalLeadStatus | '') => void;
  onChangeCreatedFrom: (value: string) => void;
  onChangeCreatedTo: (value: string) => void;
  onReset: () => void;
}

const STATUS_OPTIONS_COMMON: { value: ExternalLeadStatus; label: string }[] = [
  { value: 'pending', label: 'In sospeso' },
  { value: 'completed by user', label: 'Completata da utente' },
];

const STATUS_OPTION_WORKER: { value: ExternalLeadStatus; label: string } = {
  value: 'completed by worker',
  label: 'Completata da worker',
};

const ExternalLeadsFiltersBar = ({
  product,
  q,
  status,
  createdFrom,
  createdTo,
  onChangeQ,
  onChangeStatus,
  onChangeCreatedFrom,
  onChangeCreatedTo,
  onReset,
}: IProps) => {
  const [localQ, setLocalQ] = useState(q);

  useEffect(() => {
    setLocalQ(q);
  }, [q]);

  const debouncedOnChangeQ = useMemo(
    () => _.debounce((value: string) => onChangeQ(value), 300),
    [onChangeQ]
  );

  useEffect(() => () => debouncedOnChangeQ.cancel(), [debouncedOnChangeQ]);

  const handleChangeQ = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setLocalQ(next);
      debouncedOnChangeQ(next);
    },
    [debouncedOnChangeQ]
  );

  const statusOptions = useMemo(() => {
    if (product === 'mortgage') return STATUS_OPTIONS_COMMON;
    return [...STATUS_OPTIONS_COMMON, STATUS_OPTION_WORKER];
  }, [product]);

  const fromDate = createdFrom ? new Date(createdFrom) : null;
  const toDate = createdTo ? new Date(createdTo) : null;

  const handleChangeFrom = (value: Date | null) => {
    if (!value) {
      onChangeCreatedFrom('');
      return;
    }
    const start = new Date(value);
    start.setHours(0, 0, 0, 0);
    onChangeCreatedFrom(start.toISOString());
  };

  const handleChangeTo = (value: Date | null) => {
    if (!value) {
      onChangeCreatedTo('');
      return;
    }
    const end = new Date(value);
    end.setHours(23, 59, 59, 999);
    onChangeCreatedTo(end.toISOString());
  };

  const hasActiveFilters =
    !!q || !!status || !!createdFrom || !!createdTo;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
        px: 4,
        py: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <TextField
        size="small"
        placeholder="Cerca per nome, cognome, email o telefono"
        value={localQ}
        onChange={handleChangeQ}
        sx={{ minWidth: 280, flex: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel id="external-leads-status-label">Status</InputLabel>
        <Select
          labelId="external-leads-status-label"
          label="Status"
          value={status}
          onChange={(e) =>
            onChangeStatus(e.target.value as ExternalLeadStatus | '')
          }
        >
          <MenuItem value="">
            <em>Tutti</em>
          </MenuItem>
          {statusOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <DatePicker
        label="Da"
        value={fromDate}
        onChange={handleChangeFrom}
        slotProps={{
          textField: { size: 'small', sx: { minWidth: 160 } },
          field: { clearable: true },
        }}
        maxDate={toDate ?? undefined}
      />

      <DatePicker
        label="A"
        value={toDate}
        onChange={handleChangeTo}
        slotProps={{
          textField: { size: 'small', sx: { minWidth: 160 } },
          field: { clearable: true },
        }}
        minDate={fromDate ?? undefined}
      />

      {hasActiveFilters && (
        <Button onClick={onReset} size="small" sx={{ textTransform: 'none' }}>
          Reset
        </Button>
      )}
    </Box>
  );
};

export default ExternalLeadsFiltersBar;
